import { getBrowseById } from "#skyward_browseList";
import { getValue } from "./skyward.ui.controls";
import { registerEvents } from "./skyward.ui.events";
import { Browse } from "./types";

const events =
{
    init
};

registerEvents("checkboxBrowse", events);

const classNames =
{
    checkboxBrowse: "checkboxBrowse",
    selectAll: "selectAll",
    rowSelectCheckbox: "rowSelectCheckbox",
    checkbox: "checkbox",
    checkboxBrowseContent: "checkboxBrowseContent"
};

export interface CheckboxData
{
    Id: string;
}

export interface CheckboxBrowse extends Browse
{
    serializedOverriddenCheckboxDatas?: string | undefined;
    deserializedOverriddenCheckboxDatas?: CheckboxData[] | undefined;
    originalDefaultChecked: boolean;
    defaultChecked: boolean;
    checkboxColumnHeaderText: string;
    checkboxColumnWidth: number;
    workflowVariableName: string;
    taskInstanceID: number;
    workflowCheckboxHash: string;
}

interface OnSelectAllData
{
    browse: CheckboxBrowse;
    checkboxes: JQuery<HTMLElement>;
}

interface OnCheckboxChangeData
{
    browse: CheckboxBrowse;
    selectAllCheckbox: JQuery<HTMLElement>;
    checkboxes: JQuery<HTMLElement>;
}

export function getData(browse: CheckboxBrowse)
{
    const data =
    {
        id: browse.id,
        data: browse.data,
        browseName: browse.browseName,
        originalDefaultChecked: browse.originalDefaultChecked,
        defaultChecked: browse.defaultChecked,
        checkboxColumnHeaderText: browse.checkboxColumnHeaderText,
        checkboxColumnWidth: browse.checkboxColumnWidth,
        workflowVariableName: browse.workflowVariableName,
        serializedOverriddenCheckboxDatas: getSerializedOverriddenCheckboxDatas(browse),
        taskInstanceID: browse.taskInstanceID,
        dynamicFilterData: JSON.stringify(browse.dynamicFilterData),
    } as unknown as CheckboxBrowse;

    return data;
}

export function getSerializedOverriddenCheckboxDatas(browse: CheckboxBrowse)
{
    return (browse.deserializedOverriddenCheckboxDatas !== undefined)
        ? JSON.stringify(browse.deserializedOverriddenCheckboxDatas)
        : browse.serializedOverriddenCheckboxDatas;
}

function getOverriddenCheckboxData(browse: CheckboxBrowse)
{
    if (browse.deserializedOverriddenCheckboxDatas)
    {
        browse.serializedOverriddenCheckboxDatas = undefined;
        return browse.deserializedOverriddenCheckboxDatas;
    }

    const overriddenCheckboxData = JSON.parse(browse.serializedOverriddenCheckboxDatas!) as CheckboxData[] | undefined;
    return overriddenCheckboxData ?? [];
}

function changeSelectAll(selectAllCheckbox: JQuery<HTMLElement>, checkboxes: JQuery<HTMLElement>, browse: CheckboxBrowse, newChecked: boolean)
{
    selectAllCheckbox.prop("checked", newChecked);

    checkboxes.each(function ()
    {
        const checkbox = $(this);
        const currentlyChecked = checkbox.is(":checked");

        checkbox.prop("checked", newChecked);
        if (currentlyChecked != newChecked && !!checkbox.data("dependents"))
        {
            checkbox.trigger("change");
        }
    })

    selectAllCheckbox.prop("indeterminate", false);

    browse.deserializedOverriddenCheckboxDatas = [];
    browse.defaultChecked = newChecked;
}

function onCheckboxChange(this: JQuery<HTMLElement>, event: JQuery.ChangeEvent<HTMLElement, OnCheckboxChangeData>)
{
    const checkbox = $(this);
    const browse = event.data.browse;
    const selectAllCheckbox = event.data.selectAllCheckbox;

    const id = checkbox.data("target-id") as string;
    const overriddenCheckboxData = getOverriddenCheckboxData(browse);

    const checked = getValue(checkbox) as boolean;
    if (checked !== browse.defaultChecked)
    {
        const newData =
        {
            Id: id
        };
        overriddenCheckboxData.push(newData);
    }
    else
    {
        for (let i = 0; i < overriddenCheckboxData.length; i++)
        {
            if (overriddenCheckboxData[i].Id === id)
            {
                overriddenCheckboxData.splice(i, 1);
                break;
            }
        }
    }

    if (overriddenCheckboxData.length === 0)
    {
        changeSelectAll(selectAllCheckbox, event.data.checkboxes, browse, browse.defaultChecked);
    }
    else if (overriddenCheckboxData.length === parseInt(selectAllCheckbox.data('checkbox-count')))
    {
        changeSelectAll(selectAllCheckbox, event.data.checkboxes, browse, !browse.defaultChecked);
    }
    else
    {
        browse.deserializedOverriddenCheckboxDatas = overriddenCheckboxData;
        selectAllCheckbox.prop("indeterminate", true);
    }

    checkbox.trigger("focus");
}

function selectAllCheckboxIsIndeterminate(selectAllCheckbox: JQuery<HTMLElement>, overriddenCheckboxData: CheckboxData[])
{
    return (overriddenCheckboxData.length !== 0) && (overriddenCheckboxData.length !== parseInt(selectAllCheckbox.data("checkbox-count")));
}

function selectAllCheckboxIsChecked(selectAllCheckbox: JQuery<HTMLElement>, overriddenCheckboxData: CheckboxData[], isIndeterminate: boolean | undefined)
{
    isIndeterminate = isIndeterminate ?? selectAllCheckboxIsIndeterminate(selectAllCheckbox, overriddenCheckboxData);
    return !isIndeterminate && selectAllCheckbox.is(":checked");
}

function onSelectAllChange(this: HTMLElement, event: JQuery.ClickEvent<HTMLElement, OnSelectAllData>)
{
    const selectAllCheckbox = $(this);
    const checkboxes = event.data.checkboxes;
    const browse = event.data.browse;

    const overriddenCheckboxData = getOverriddenCheckboxData(browse);

    const checked: boolean = selectAllCheckboxIsChecked(selectAllCheckbox, overriddenCheckboxData, undefined);

    changeSelectAll(selectAllCheckbox, checkboxes, browse, checked);
}

function init(this: JQuery<HTMLElement>)
{
    const checkboxBrowse = $(this);
    if (checkboxBrowse.data("checkboxBrowseLoaded") === true)
    {
        return;
    }

    const browseName = checkboxBrowse.attr("id");
    checkboxBrowse.on(`browseLoaded.${browseName}`, function ()
    {
        setup(checkboxBrowse);
        checkboxBrowse.data("checkboxBrowseLoaded", true);
    });
}

function setup(checkboxBrowse: JQuery<HTMLElement>)
{
    const browseName = checkboxBrowse.attr("id");
    const browse = getBrowseById(browseName) as CheckboxBrowse;

    const selectAllCheckbox = checkboxBrowse.find(`.${classNames.selectAll}`);
    const checkboxes = checkboxBrowse.find(`.${classNames.rowSelectCheckbox}`).not(selectAllCheckbox);

    browse.deserializedOverriddenCheckboxDatas = undefined;

    selectAllCheckbox.off('click', onSelectAllChange);

    const onSelectAllChangeData: OnSelectAllData = { browse, checkboxes };
    selectAllCheckbox.on("click", onSelectAllChangeData, onSelectAllChange);

    checkboxBrowse.off('change', `.${classNames.checkbox}.${classNames.checkboxBrowseContent}.${classNames.rowSelectCheckbox}`, onCheckboxChange);
    checkboxBrowse.on('change', `.${classNames.checkbox}.${classNames.checkboxBrowseContent}.${classNames.rowSelectCheckbox}`, { browse: browse, checkboxes: checkboxes, selectAllCheckbox: selectAllCheckbox }, onCheckboxChange);
}
